import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  HStack,
  Text,
  VStack,
  Card,
  Grid,
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuPositioner,
  MenuTrigger
} from '@chakra-ui/react';
import { Button } from '@/components/ui/button';
import { fetchAPI } from '@/api/fetch';
import {
  apiAdminGetAnalysisProviders,
  apiAdminUpdateAnalysisProvider
} from '@/api/services/admin.service';
import { AnalysisProvider } from '@/types/AnalysisProvider';
import { PromptKey } from '@/types/enums/PromptKey';
import { LlmModelProvider } from '@/types/enums/LlmModelProvider';

const getAnalysisDisplayName = (analysisName: PromptKey): string => {
  const displayNames: Record<PromptKey, string> = {
    [PromptKey.JDExtractor]: 'Job Description Extractor',
    [PromptKey.ResumeExtractor]: 'Resume Extractor',
    [PromptKey.JDRequirements]: 'Job Requirements Analyzer',
    [PromptKey.ResumeAnalyzer]: 'Resume Analyzer',
    [PromptKey.ResumeJobPositionFit]: 'Resume Job Position Fit',
    [PromptKey.InterviewAnalyzer]: 'Interview Analyzer',
    [PromptKey.OverviewAnalyzer]: 'Overview Analyzer',
    [PromptKey.InterviewGenerator]: 'Interview Generator',
    [PromptKey.JobResumeMatching]: 'Job Resume Matching',
    [PromptKey.ScreeningAnalyzerJobInterview]: 'Screening Analyzer (Job Interview)',
    [PromptKey.ScreeningGeneratorJobInterview]: 'Screening Generator (Job Interview)',
    [PromptKey.ScreeningAnalyzerResumeScreening]: 'Screening Analyzer (Resume Screening)',
    [PromptKey.ScreeningGeneratorResumeScreening]: 'Screening Generator (Resume Screening)',
    [PromptKey.QuestionBankExtractor]: 'Question Bank Extractor',
    [PromptKey.VeteransResumeExtractor]: 'Veterans Resume Extractor',
    [PromptKey.VeteransResumeGenerator]: 'Veterans Resume Generator',
    [PromptKey.EmbeddingVector]: 'Embedding Vector',
    [PromptKey.MilitaryTranslationSample]: 'Military Translation Sample'
  };

  return displayNames[analysisName] || analysisName;
};

const getProviderDisplayName = (provider: LlmModelProvider): string => {
  const displayNames: Record<LlmModelProvider, string> = {
    [LlmModelProvider.OpenAI]: 'OpenAI',
    [LlmModelProvider.Gemini]: 'Gemini'
  };

  return displayNames[provider] || provider;
};

const getProviderLabelColor = (provider: LlmModelProvider): string => {
  switch (provider) {
    case LlmModelProvider.OpenAI:
      return 'red.500';
    case LlmModelProvider.Gemini:
      return 'blue.500';
    default:
      return 'gray.500';
  }
};

const categorizeAnalysisProviders = (providers: AnalysisProvider[]) => {
  const categories = {
    extractor: [] as AnalysisProvider[],
    analyzer: [] as AnalysisProvider[],
    generator: [] as AnalysisProvider[],
    matching: [] as AnalysisProvider[],
    embedding: [] as AnalysisProvider[],
    other: [] as AnalysisProvider[]
  };

  providers.forEach((provider) => {
    switch (provider.analysis_name) {
      case PromptKey.JDExtractor:
      case PromptKey.ResumeExtractor:
      case PromptKey.QuestionBankExtractor:
      case PromptKey.VeteransResumeExtractor:
        categories.extractor.push(provider);
        break;
      case PromptKey.JDRequirements:
      case PromptKey.ResumeAnalyzer:
      case PromptKey.InterviewAnalyzer:
      case PromptKey.ScreeningAnalyzerJobInterview:
      case PromptKey.ScreeningAnalyzerResumeScreening:
      case PromptKey.OverviewAnalyzer:
      case PromptKey.VeteransResumeGenerator:
        categories.analyzer.push(provider);
        break;
      case PromptKey.InterviewGenerator:
      case PromptKey.ScreeningGeneratorJobInterview:
      case PromptKey.ScreeningGeneratorResumeScreening:
        categories.generator.push(provider);
        break;
      case PromptKey.JobResumeMatching:
      case PromptKey.ResumeJobPositionFit:
        categories.matching.push(provider);
        break;
      case PromptKey.EmbeddingVector:
        categories.embedding.push(provider);
        break;
      default:
        categories.other.push(provider);
        break;
    }
  });

  return categories;
};

export default function AnalysisProviderPage() {
  const [providers, setProviders] = useState<AnalysisProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchProviders = async () => {
    try {
      setError(null);
      const response = await fetchAPI<AnalysisProvider[]>({
        request: () => apiAdminGetAnalysisProviders()
      });

      if (response.success) {
        setProviders(response.data);
      } else {
        setError('Failed to fetch analysis providers');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setError(`Failed to fetch analysis providers: ${errorMessage}`);
      console.error('Failed to fetch analysis providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProvider = async (analysisName: PromptKey, newProvider: LlmModelProvider) => {
    setUpdating(analysisName);
    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiAdminUpdateAnalysisProvider({
            payload: {
              analysis_name: analysisName,
              provider: newProvider
            }
          })
      });

      if (response.success) {
        setProviders((prev) =>
          prev.map((p) => (p.analysis_name === analysisName ? { ...p, provider: newProvider } : p))
        );
      } else {
        setError('Failed to update analysis provider');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      setError(`Failed to update analysis provider: ${errorMessage}`);
      console.error('Failed to update analysis provider:', error);
    } finally {
      setUpdating(null);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  if (loading) {
    return (
      <Container maxW="container.xl" p={6}>
        <VStack align="stretch" gap={6}>
          <Text fontSize="2xl" fontWeight="semibold">
            Analysis Providers
          </Text>
          <Text>Loading...</Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.xl" p={6}>
        <VStack align="stretch" gap={6}>
          <Text fontSize="2xl" fontWeight="semibold">
            Analysis Providers
          </Text>
          <Box p={4} borderRadius="md" bg="red.50" borderColor="red.200" borderWidth={1}>
            <Text color="red.600">{error}</Text>
            <Button
              mt="12px"
              onClick={fetchProviders}
              fontSize="16px"
              border="1px solid"
              borderRadius="8px"
              borderColor="primary.900"
              color="primary.900"
              bg="neutrals.0"
              _hover={{ bg: 'neutrals.50' }}
            >
              Retry
            </Button>
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" p={6}>
      <VStack align="stretch" gap={6}>
        <Text fontSize="2xl" fontWeight="semibold">
          Analysis Providers
        </Text>

        <Text fontSize="14px" color="gray.600">
          Configure which LLM provider to use for each analysis type.
        </Text>

        {(() => {
          const categories = categorizeAnalysisProviders(providers);
          const categoryDisplayNames = {
            extractor: 'Extractors',
            analyzer: 'Analyzers',
            generator: 'Generators',
            matching: 'Matching',
            embedding: 'Embeddings',
            other: 'Other'
          };

          return Object.entries(categories).map(([categoryKey, categoryProviders]) => {
            if (categoryProviders.length === 0) return null;

            return (
              <VStack key={categoryKey} align="stretch" gap={4}>
                <Text fontSize="xl" fontWeight="semibold" color="gray.800">
                  {categoryDisplayNames[categoryKey as keyof typeof categoryDisplayNames]}
                </Text>

                <Grid
                  templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
                  gap={4}
                >
                  {categoryProviders.map((provider) => (
                    <Card.Root key={provider.analysis_id}>
                      <Card.Body>
                        <VStack align="stretch" gap={3}>
                          <Text fontSize="18px" fontWeight="medium" lineHeight="short">
                            {getAnalysisDisplayName(provider.analysis_name)}
                          </Text>

                          <HStack justify="space-between">
                            <MenuRoot>
                              <MenuTrigger asChild>
                                <Button
                                  variant="outline"
                                  border="1px solid"
                                  borderColor="neutrals.300"
                                  borderRadius="8px"
                                  fontSize="14px"
                                  size="sm"
                                  color={getProviderLabelColor(provider.provider)}
                                  disabled={updating === provider.analysis_name}
                                >
                                  {getProviderDisplayName(provider.provider)}
                                </Button>
                              </MenuTrigger>
                              <MenuPositioner>
                                <MenuContent>
                                  <MenuItem
                                    fontSize="14px"
                                    cursor="pointer"
                                    value={LlmModelProvider.OpenAI}
                                    onClick={() =>
                                      handleUpdateProvider(
                                        provider.analysis_name,
                                        LlmModelProvider.OpenAI
                                      )
                                    }
                                  >
                                    {getProviderDisplayName(LlmModelProvider.OpenAI)}
                                  </MenuItem>
                                  <MenuItem
                                    fontSize="14px"
                                    cursor="pointer"
                                    value={LlmModelProvider.Gemini}
                                    onClick={() =>
                                      handleUpdateProvider(
                                        provider.analysis_name,
                                        LlmModelProvider.Gemini
                                      )
                                    }
                                  >
                                    {getProviderDisplayName(LlmModelProvider.Gemini)}
                                  </MenuItem>
                                </MenuContent>
                              </MenuPositioner>
                            </MenuRoot>

                            {updating === provider.analysis_name && (
                              <Text fontSize="sm" color="blue.600">
                                Updating...
                              </Text>
                            )}
                          </HStack>
                        </VStack>
                      </Card.Body>
                    </Card.Root>
                  ))}
                </Grid>
              </VStack>
            );
          });
        })()}

        {providers.length === 0 && (
          <Box p={6} textAlign="center" color="gray.500">
            <Text>No analysis providers found.</Text>
          </Box>
        )}
      </VStack>
    </Container>
  );
}
